// DO NOT EDIT -- generated from fs/api/proactiveinsights/alertconfig.proto

import { DxIdVersion } from './dxobjects.proto';
import * as protobuf from '../../../google/protobuf';

/**
 * Defines the configuration for FullStory to monitor a given dx object and notify the user upon an insights algorithm
 * detecting meaningful changes.
 * 
 * NOTE FOR DEVELOPERS: If you add a new field that won't materially change what alerts are generated, please blocklist
 * them from the hash computation in util.go
 * ToDo(PRO-117): Create a test to automatically detect the above
 */
export interface AlertConfig {
  /**
   * A server-generated unique ID that can be used to reference this alert config.
   * IDs are only guaranteed to be unique within an individual Org.
   */
  id: string;
  /**
   * A server-generated hash that can be used to determine whether this config has changed in ways that would alter
   * what alerts are generated. For now, this excludes any references to dependent object versions
   * (i.e. DxIdVersion.Version).
   * 
   * For example, the hash would include the algorithm configurations as changes that will produce different alerts.
   * However, it would exclude the description field since description changes wouldn't meaningfully affect alert
   * production.
   * 
   * NOTE: This hash excludes the config id. Hashes are not unique across configs. Hence, the hash can be used to
   * determine if two different alert configs would produce similar results (again dependent object versions would need
   * to be checked separately).
   */
  hash: string;
  /**
   * The last time that the config hash was updated. This indicates the last time that the config was updated in a way
   * that would meaningfully affect the generated alerts, as opposed to an update to the name or description of the
   * alert. This time is set by the server to nil when the config is initially created, and then set to the current time
   * whenever the config is updated.
   */
  lastHashChange: protobuf.timestamp | null;
  /** The name or title of the Alert Config. This is a helpful reminder to customers of the purpose of the config. */
  name: string;
  /** The purpose of the alert and what it is attempting to achieve. */
  description: string;
  /**
   * The id of the dx object to alert on.
   * 
   * Alerts only apply to the most recent version, but tracking the version is useful for
   * determining whether the dx object definition changed. If it has changed, we will verify
   * that the config is still valid. If the change is invalid this will be the version of the last
   * supported dx object.
   * For now, we will store the Version field from DX Objects which is an opaque id that does support
   * comparisons (i.e. newer versions will be greater than older versions)
   */
  dxIdVersion: DxIdVersion | null;
  /**
   * The version of the algorithm we are using to calculate the alert. As we update
   * our algorithms, we may produce newer versions incompatible with older ones.
   * Hence, this is added to make it easier to do migrations. Each algorithm type will
   * have its own version system.
   */
  algorithmVersion: number;
  /**
   * The Time Zone that we should apply when querying data for this alert. If this is not specified, we will
   * default to UTC. As an implementation detail, this field can be changed over the config's lifetime.
   */
  timeZone: string;
  /** if (x) > t --> alert */
  absoluteThreshold?: AbsoluteThresholdConfig | null;
  /**  if (x2-x1)/x1 >= t --> alert */
  relativeChangeThreshold?: RelativeChangeThresholdConfig | null;
  /** if (x2-x1) >= t --> alert */
  absoluteChangeThreshold?: AbsoluteChangeThresholdConfig | null;
}

/**
 * Defines the configuration for FullStory to monitor a given dx object and notify the user upon an insights algorithm
 * detecting meaningful changes.
 * 
 * NOTE FOR DEVELOPERS: If you add a new field that won't materially change what alerts are generated, please blocklist
 * them from the hash computation in util.go
 * ToDo(PRO-117): Create a test to automatically detect the above
 */
export namespace AlertConfig {
  /**
   * It is the responsibility of consumers of this API to ensure that they don't select an algorithm type that is
   * unsupported by the current DX Object.
   */
  export enum algorithm {
    /** if (x) > t --> alert */
    absoluteThreshold = 8,
    /**  if (x2-x1)/x1 >= t --> alert */
    relativeChangeThreshold = 11,
    /** if (x2-x1) >= t --> alert */
    absoluteChangeThreshold = 12
  }

  /**
   * It is the responsibility of consumers of this API to ensure that they don't select an algorithm type that is
   * unsupported by the current DX Object.
   */
  export function algorithm_oneof(m: AlertConfig): { opt: algorithm.absoluteChangeThreshold, val: AbsoluteChangeThresholdConfig } | { opt: algorithm.absoluteThreshold, val: AbsoluteThresholdConfig } | { opt: algorithm.relativeChangeThreshold, val: RelativeChangeThresholdConfig } | null {
    if (m.absoluteThreshold != null) { return { opt: algorithm.absoluteThreshold, val: m.absoluteThreshold } }
    if (m.relativeChangeThreshold != null) { return { opt: algorithm.relativeChangeThreshold, val: m.relativeChangeThreshold } }
    if (m.absoluteChangeThreshold != null) { return { opt: algorithm.absoluteChangeThreshold, val: m.absoluteChangeThreshold } }
    return null;
  }
}

/**
 * AlertDirection is used for alert configuration to specify when an alert
 * should be triggered and may include `DIRECTION_INCREASE_OR_DECREASE`.
 */
export enum AlertDirection {
  ALERT_DIRECTION_INVALID = 0,
  ALERT_DIRECTION_INCREASING = 1,
  ALERT_DIRECTION_DECREASING = 2,
  /** either increasing or decreasing would trigger alert */
  ALERT_DIRECTION_INCREASE_OR_DECREASE = 3
}

/**
 * The operator to compare the value to the threshold to determine if the
 * alert should trigger.
 */
export enum ThresholdOperation {
  THRESHOLD_OPERATION_INVALID_OPERATION = 0,
  THRESHOLD_OPERATION_GREATER_THAN = 1,
  THRESHOLD_OPERATION_LESS_THAN = 3
}

/**
 * A configuration to leverage in the event that the user wishes us to alert them
 * when an absolute threshold is crossed. An absolute threshold is simply looking at
 * the value itself and seeing if it crosses the threshold.
 */
export interface AbsoluteThresholdConfig {
  /**
   * The threshold that must be crossed in order for the alert to fire. This is represented
   * as a double so that we can support fractional values.
   * Percentages are represented as whole numbers versus decimals. Thus, 0.4 will be 0.4%.
   */
  threshold: number;
  /**
   * The operator to compare the value to the threshold to determine if the
   * alert should trigger.
   * By default, we will only trigger alerts when the threshold is crossed.
   */
  operation: ThresholdOperation;
  /**
   * Whether the alert should continue to trigger if the data remains beyond the threshold versus only
   * when the threshold is crossed.
   */
  triggerWhileThresholdCrossed: boolean;
}

/**
 * A configuration to leverage in the event that the user wishes us to alert them
 * when an absolute change threshold is reached (equal to) or crossed.
 * An absolute change is computed by comparing the current data point (x2) to the
 * previous data point (x1) and seeing if the difference between the two exceeded the threshold
 * in the direction specified (increase, decrease, increasing_or_decreasing):
 * e.g. user sets threshold=30 and direction=increasing :
 *    if (x2-x1) >= 30 --> alert
 */
export interface AbsoluteChangeThresholdConfig {
  /**
   * The threshold that must be crossed in order for the alert to fire. This is represented
   * as a double so that we can support fractional values.
   * Percentages are represented as whole numbers versus decimals. Thus, 0.4 will be 0.4%.
   */
  threshold: number;
  /**
   * An INCREASING direction means we will only alert if the newer data point is increasing
   * compared to the previous one. INCREASE_OR_DECREASE means to alert if the difference is
   * bigger or equal to the threshold in either direction.
   */
  direction: AlertDirection;
}

/**
 * A configuration to leverage in the event that the user wishes us to alert them
 * when a relative change threshold is reached (equal to) or crossed.
 * A relative change is computed by comparing the current data point (x2) to the
 * previous data point (x1) and seeing if the difference between the two exceeded,
 * relative to the previous data point, exceeds the threshold in the direction specified:
 * e.g. user sets threshold=30 and direction=increasing:
 *    if (x2-x1)/x1 >= 30 --> alert
 */
export interface RelativeChangeThresholdConfig {
  /**
   * The threshold that must be crossed in order for the alert to fire. This is represented
   * as a double so that we can support fractional values.
   * Percentages are represented as whole numbers versus decimals. Thus, 0.4 will be 0.4%.
   */
  threshold: number;
  /**
   * An INCREASING direction means we will only alert if the newer data point is increasing
   * compared to the previous one. INCREASE_OR_DECREASE means to alert if the difference is
   * bigger or equal to the threshold in either direction.
   */
  direction: AlertDirection;
}
